import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import Button from '../../../../../components/ui/Button';
import InputField from '../../../../../components/ui/InputField';
import { CustomTheme, ThemeProperties } from '../../../../../types/customTheme';

interface ThemeFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (theme: ThemeProperties | (ThemeProperties & { id: string })) => void;
	theme?: CustomTheme | null;
	isLoading?: boolean;
}

const defaultTheme: ThemeProperties = {
	name: '',
	primaryColor: '#0ea5e9',
	secondaryColor: '#06b6d4',
	accentColor: '#3b82f6',
	backgroundColor: '#f8fafc',
	textColor: '#1e293b',
	cardColor: '#ffffff',
	borderColor: '#e2e8f0'
};

const ThemeFormModal: React.FC<ThemeFormModalProps> = ({
	isOpen,
	onClose,
	onSave,
	theme,
	isLoading = false
}) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';
	const [formData, setFormData] = useState<ThemeProperties>(defaultTheme);

	useEffect(() => {
		if (theme) {
			setFormData(theme.theme);
		} else {
			setFormData(defaultTheme);
		}
	}, [theme, isOpen]);

	const handleChange = (field: keyof ThemeProperties, value: string) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (theme?.id) {
			onSave({ ...formData, id: theme.id });
		} else {
			onSave(formData);
		}
	};

	if (!isOpen) return null;

	const colorFields: { key: keyof ThemeProperties; labelKey: string }[] = [
		{ key: 'primaryColor', labelKey: 'settings.primaryColor' },
		{ key: 'secondaryColor', labelKey: 'settings.secondaryColor' },
		{ key: 'accentColor', labelKey: 'settings.accentColor' },
		{ key: 'backgroundColor', labelKey: 'settings.backgroundColor' },
		{ key: 'textColor', labelKey: 'settings.textColor' },
		{ key: 'cardColor', labelKey: 'settings.cardColor' },
		{ key: 'borderColor', labelKey: 'settings.borderColor' }
	];

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
				{/* Backdrop */}
				<div
					className="fixed inset-0 transition-opacity bg-gray-500/75 dark:bg-gray-900/75"
					onClick={onClose}
				/>

				{/* Modal */}
				<div className="relative inline-block w-full max-w-lg p-4 sm:p-6 mx-2 sm:mx-0 overflow-hidden text-start align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-xl">
					{/* Header */}
					<div className="flex items-center justify-between mb-4 sm:mb-6">
						<h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
							{theme ? t('settings.editTheme') : t('settings.addTheme')}
						</h3>
						<button
							onClick={onClose}
							className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
						>
							<FiX className="w-5 h-5" />
						</button>
					</div>

					<form onSubmit={handleSubmit}>
						{/* Theme Name */}
						<div className="mb-4">
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								{t('settings.themeName')}
							</label>
							<InputField
								value={formData.name}
								onChange={(e) => handleChange('name', e.target.value)}
								placeholder={t('settings.themeNamePlaceholder')}
								required
							/>
						</div>

						{/* Color Preview */}
						<div className="mb-4 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700">
							<p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
								{t('settings.themePreview')}
							</p>
							<div className="flex flex-wrap gap-2">
								{colorFields.slice(0, 4).map(({ key }) => (
									<div
										key={key}
										className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-gray-300 dark:border-gray-600"
										style={{ backgroundColor: formData[key] as string }}
										title={key}
									/>
								))}
							</div>
						</div>

						{/* Color Pickers */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 max-h-48 sm:max-h-60 overflow-y-auto">
							{colorFields.map(({ key, labelKey }) => (
								<div key={key}>
									<label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
										{t(labelKey)}
									</label>
									<div className="flex gap-2">
										<input
											type="color"
											value={formData[key] as string}
											onChange={(e) => handleChange(key, e.target.value)}
											className="w-10 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
										/>
										<InputField
											value={formData[key] as string}
											onChange={(e) => handleChange(key, e.target.value)}
											className={`flex-1 text-xs ${isRTL ? 'text-end' : ''}`}
											dir="ltr"
										/>
									</div>
								</div>
							))}
						</div>

						{/* Actions */}
						<div className="flex justify-end gap-3">
							<Button
								type="button"
								variant="ghost"
								onClick={onClose}
								disabled={isLoading}
							>
								{t('common.cancel')}
							</Button>
							<Button
								type="submit"
								variant="primary"
								isLoading={isLoading}
								disabled={!formData.name.trim()}
							>
								{t('settings.saveTheme')}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default ThemeFormModal;
